<script setup lang="ts">
import type { BlockList } from '@/models'
import { type PropType, ref, computed } from 'vue'
import { Carousel, Slide } from 'vue3-carousel'
import FlexibleBlock from '../../blockStyles/FlexibleBlock.vue'
import QuotationBlock from '../../blockStyles/QuotationBlock.vue'
import 'vue3-carousel/carousel.css'
import { mainBackgroundClasses } from '@/constants'
import RightArrow from '@/assets/rightArrow.svg'

const props = defineProps({
  blockList: {
    type: Object as PropType<BlockList>,
    required: true,
  },
  isHeaderBlock: {
    type: Boolean,
    default: false,
  },
})

const blockViewList = {
  flexible: FlexibleBlock,
  quotation: QuotationBlock,
}

const isStudentPerspective = computed(() => props.blockList.blockId?.includes('student') === true)

const listStyleGuess = props.blockList.blocks[0]?.style ?? ''

const carouselConfigCase = {
  autoplay: isStudentPerspective.value ? 0 : 5000,
  transition: 1000,
  itemsToShow: 1.5,
  wrapAround: true,
  snapAlign: 'start' as const,
  gap: 2,
  breakpoints: {
    640: {
      // sm
      itemsToShow: 1.5,
    },
    1024: {
      // lg
      itemsToShow: 2.5,
    },
    1920: {
      // 3xl
      itemsToShow: 3.5,
    },
  },
}

const isCaseExperience = computed(() => props.blockList.blockId?.includes('experience') === true)

const carouselConfigQuotation = {
  autoplay: 7000,
  transition: 1000,
  itemsToShow: isCaseExperience.value ? 1 : 1.5,
  wrapAround: true,
  snapAlign: 'center' as const,
  gap: 10,
  breakpoints: {
    768: {
      itemsToShow: isCaseExperience.value ? 2 : 3.5,
    },
    1720: {
      itemsToShow: isCaseExperience.value ? 3 : 7.5,
    },
  },
}

const carouselConfig = listStyleGuess === 'quotation' ? carouselConfigQuotation : carouselConfigCase

const carousel = ref()
const currentSlide = ref(0)

function SlideRight() {
  carousel.value?.next()
}

function SlideLeft() {
  carousel.value?.prev()
}

function listItemcustomClasses(index = 0) {
  const blurGradient =
    "after:bg-radial after:from-[#FFD8FC] after:to-[#FF41F2] after:content-[''] after:absolute after:-bottom-[25%] after:right-[25%] after:w-64 after:h-64 after:blur-[150px]  [@media(min-resolution:2dppx)]:after:w-48 [@media(min-resolution:2dppx)]:after:h-48"

  switch (props.blockList.subStyle) {
    case 'none':
      return 'rounded-xl'
    case 'one':
      if (index % 3 === 0)
        return `relative w-full h-[24rem] rounded-3xl text-white bg-gradient-to-r from-[#123A35] to-[#1A635F] ${blurGradient}`
      else if (index % 3 === 1)
        return `relative w-full h-[24rem] rounded-3xl text-white bg-gradient-to-r from-[#FED046] to-[#734508] ${blurGradient}`
      else
        return `relative w-full h-[24rem] rounded-3xl text-white bg-gradient-to-r from-[#5FAACD] to-[#93B5CE] ${blurGradient}`
    default:
      return ''
  }
}
</script>

<template>
  <div>
    <FlexibleBlock
      :blockData="blockList.details"
      :isHeaderBlock="isHeaderBlock"
      :customClass="mainBackgroundClasses(blockList.details.backgroundColour)"
    />
    <Carousel
      ref="carousel"
      v-model="currentSlide"
      v-bind="carouselConfig"
      :class="['flex flex-row', isCaseExperience ? 'experience-carousel-bleed' : '']"
    >
      <Slide
        v-for="(block, index) in blockList.blocks"
        :key="block.id"
        :class="{
          'translate-x-24': blockList.subStyle === 'one',
          'is-odd': index % 2 === 1,
          'is-even': index % 2 === 0,
        }"
      >
        <component
          :class="['m-2', isCaseExperience ? 'rounded-xl' : '']"
          :subStyle="blockList.subStyle"
          :blockData="block"
          :isHeaderBlock="false"
          :customClass="listItemcustomClasses(index)"
          :is="blockViewList[block.style as keyof typeof blockViewList]"
        />
      </Slide>
    </Carousel>
    <div v-if="blockList.navigation" class="flex flex-row m-16">
      <div
        @click="SlideLeft()"
        :class="[
          'w-12 h-12 rounded-full flex items-center justify-center cursor-pointer',
          isCaseExperience ? 'bg-blue' : 'bg-green',
        ]"
      >
        <img :src="RightArrow" class="w-4 h-4 invert rotate-180" />
      </div>
      <div
        @click="SlideRight()"
        :class="[
          'w-12 h-12 ml-1 rounded-full flex items-center justify-center cursor-pointer',
          isCaseExperience ? 'bg-blue' : 'bg-green',
        ]"
      >
        <img :src="RightArrow" class="w-4 h-4 invert" />
      </div>
    </div>
  </div>
</template>

<style>
[id^='case-'][id$='-experience'] .experience-carousel-bleed {
  width: 100vw;
  max-width: 100vw;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
}

[id^='case-'][id$='-experience'] {
  overflow-x: clip;
}

[id^='case-'][id$='-experience'] .experience-carousel-bleed {
  padding-left: 3rem;
  padding-right: 3rem;
  box-sizing: border-box;
}

@media (max-width: 1300px) {
  [id^='case-'][id$='-experience'] .experience-carousel-bleed {
    padding-left: 2rem;
    padding-right: 2rem;
  }
}

@media (max-width: 640px) {
  [id^='case-'][id$='-experience'] .experience-carousel-bleed {
    padding-left: 1.25rem;
    padding-right: 1.25rem;
  }
}
</style>
